import React, { FC } from 'react'
import { Route, Switch } from 'react-router-dom'

// Constants
import { routes } from '@sketch/constants'

// Hooks
import { useBellNotifications } from 'modules/activity/operations'
import { useVersionSubscriptions } from 'modules/versioning/operations'
import { useProjectMembershipSubscriptions } from 'modules/projects/operations'
import { useFlag } from 'containers/flags'

// Containers
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import PrivateRoute from 'containers/PrivateRoute'
import MFAPrivateRoute from 'containers/MFAPrivateRoute'

// Components
import NotFoundView from 'views/NotFoundView'
import {
  AcceptShareInviteByInviteTokenView,
  AcceptShareInviteByShareShortIdView,
} from 'modules/shares/AcceptShareInviteView/'
import { ShareRoutes } from 'modules/shares/routes'
import SettingsView from 'modules/user/views/SettingsView'
import { Updates } from 'modules/user/views/Updates'
import { DesignSystem, DocPage } from 'modules/designSystems/pages'

import PersonalAuthRoute from 'modules/sso/components/PersonalAuthRoute'
import { PrivateWorkspaceRoute } from 'modules/workspace/containers'

import WorkspaceDocumentsView from 'modules/workspace/views/WorkspaceDocumentsView'
import WorkspaceSettingsView from 'modules/workspace/views/WorkspaceSettingsView'
import WorkspaceSubscribeView from 'modules/workspace/views/WorkspaceSubscribeView'
import WorkspacePartnerSubscribeView from 'modules/workspace/views/WorkspacePartnerSubscribeView'
import RecoveryCodeView from 'modules/user/views/RecoveryCodeView'

import { UserToWorkspaceRedirect } from 'modules/workspace/components/UserToWorkspaceRedirect'

import VerificationCodeView from 'modules/user/views/VerificationCodeView'

import WorkspaceProfileEditView from 'modules/workspaceProfile/views/WorkspaceProfileEditView'
import WorkspaceProfilePublicView from 'modules/workspaceProfile/views/WorkspaceProfilePublicView'

import { AcceptProjectInviteByTokenView } from 'modules/projects/views/AcceptProjectInviteByTokenView'

// Styles
import { CoreLayoutWrapper } from './CoreLayout.styles'
import ShareSubscriptions from 'modules/shares/components/ShareSubscriptions'
import { RestrictedWorkspaceBannerManagerSwitch } from 'modules/workspace/containers/RestrictedWorkspaceBannerManager'

const AcceptWorkspaceInviteView = React.lazy(
  () => import('modules/workspace/views/AcceptWorkspaceInviteView')
)

const RedeemLicenseConfirmationView = React.lazy(
  () => import('modules/workspace/views/RedeemLicenseConfirmationView')
)

const CreateWorkspaceView = React.lazy(
  () => import('modules/workspace/views/CreateWorkspaceView')
)

const CreateWorkspaceDoneView = React.lazy(
  () => import('modules/workspace/views/CreateWorkspaceDoneView')
)

const DiscoverView = React.lazy(
  () => import('modules/discover/views/DiscoverView/DiscoverView')
)

const CoreLayout: FC = () => {
  useBellNotifications()
  useVersionSubscriptions()
  useProjectMembershipSubscriptions()

  const isDesignSystemsOn = useFlag('design-systems')
  const isWorkspaceTemplatesFFOn = useFlag('set-as-template')

  // TODO: Remove sketch-partners FF
  // https://github.com/sketch-hq/Cloud/issues/12404
  const isSketchPartnersFFEnabled = useFlag('sketch-partners')

  // TODO: Remove profiles FF
  // https://github.com/sketch-hq/Cloud/issues/14920
  const isProfileFFEnabled = useFlag('profiles')

  return (
    <CoreLayoutWrapper>
      <ShareSubscriptions />
      <React.Suspense fallback={<DynamicLoadingPage />}>
        <RestrictedWorkspaceBannerManagerSwitch />
        <Switch>
          {/* Redirect from Baby Cloud to the Personal-Workspace */}
          <PrivateRoute
            path={[
              routes.PERSONAL_PROJECT.template(),
              routes.DOCUMENTS.template(),
              routes.LIBRARIES.template(),
              routes.TRASH.template(),
              routes.PERSONAL_DOCUMENTS.template(),
            ]}
            component={UserToWorkspaceRedirect}
          />

          {/* 2FA Routes */}
          <MFAPrivateRoute
            path={routes.VERIFICATION_CODE.template()}
            component={VerificationCodeView}
          />

          <MFAPrivateRoute
            path={routes.RECOVERY_CODE.template()}
            component={RecoveryCodeView}
          />

          {/* Workspace Routes */}
          <PrivateRoute
            path={routes.WORKSPACE_INVITE.template()}
            component={AcceptWorkspaceInviteView}
          />

          <PersonalAuthRoute
            fallbackRouteIfUserOnlyHasSsoAuth={({ location }) => ({
              pathname: routes.SIGN_IN.create({}),
              state: { from: location },
            })}
            path={[
              routes.WORKSPACE_CREATE.template(),
              routes.WORKSPACE_CREATE_DONE.template(),
            ]}
            render={() => (
              <>
                <PrivateRoute
                  exact
                  path={routes.WORKSPACE_CREATE_DONE.template()}
                  render={({ match }) => (
                    <CreateWorkspaceDoneView
                      workspaceId={match.params.workspaceId!}
                    />
                  )}
                />
                <PrivateRoute
                  exact
                  path={routes.WORKSPACE_CREATE.template()}
                  redirectPathIfSignOut="sign-up"
                  component={CreateWorkspaceView}
                />
              </>
            )}
          />

          <PrivateRoute
            exact
            path={routes.WORKSPACE_LICENSE_REDEEM_CONFIRM.template()}
            component={RedeemLicenseConfirmationView}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_SETTINGS.template()}
            component={WorkspaceSettingsView}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_DISCOVER.template()}
            component={DiscoverView}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_SUBSCRIBE.template()}
            component={WorkspaceSubscribeView}
          />

          {isSketchPartnersFFEnabled && (
            <PrivateRoute
              exact
              path={routes.WORKSPACE_SUBSCRIBE_PARTNER.template()}
              component={WorkspacePartnerSubscribeView}
            />
          )}

          <PrivateWorkspaceRoute
            exact
            path={[
              routes.WORKSPACE_PROJECT.template(),
              routes.WORKSPACE_SHARES.template(),
              routes.WORKSPACE_LIBRARIES.template(),
              routes.WORKSPACE_TRASH.template(),
              routes.WORKSPACE_DRAFTS.template(),
              routes.WORKSPACE_SHARED_WITH_ME.template(),
              routes.WORKSPACE_TRASH_PROJECT.template(),
            ]}
            component={WorkspaceDocumentsView}
          />

          {isWorkspaceTemplatesFFOn ? (
            <PrivateWorkspaceRoute
              exact
              path={routes.WORKSPACE_TEMPLATES.template()}
              component={WorkspaceDocumentsView}
            />
          ) : null}

          {isProfileFFEnabled ? (
            <PrivateWorkspaceRoute
              exact
              path={routes.WORKSPACE_PROFILE_EDIT.template()}
              component={WorkspaceProfileEditView}
            />
          ) : null}

          <Route
            exact
            path={routes.WORKSPACE_PROFILE.template()}
            component={WorkspaceProfilePublicView}
          />

          <PrivateRoute
            exact
            path={routes.UPDATES.template()}
            component={Updates}
          />

          {/* For now a design system can only be accessed by a logged in user
          within the same workspace, but in the future design systems could be
          public, that will imply to change PrivateRoute for Route */}
          {isDesignSystemsOn && (
            <PrivateWorkspaceRoute
              exact
              path={routes.DESIGN_SYSTEM.template()}
              component={DesignSystem}
            />
          )}
          {isDesignSystemsOn && (
            <PrivateWorkspaceRoute
              exact
              path={routes.DESIGN_SYSTEM_PAGES.template()}
              component={DocPage}
            />
          )}

          {/* Shares */}
          <Route
            path={routes.SHARE_INVITE.template()}
            component={AcceptShareInviteByShareShortIdView}
          />

          <Route
            path={routes.SHARE_ACCEPT_INVITE_ROOT.template()}
            component={AcceptShareInviteByInviteTokenView}
          />

          <Route path={routes.SHARE_VIEW.template()} component={ShareRoutes} />

          {/* Projects */}
          <Route
            path={routes.PROJECT_ACCEPT_INVITE.template()}
            component={AcceptProjectInviteByTokenView}
          />

          {/* User */}
          <PersonalAuthRoute
            exact
            path={[
              routes.PERSONAL_SETTINGS.template(),
              routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template(),
            ]}
          >
            <PrivateRoute component={SettingsView} />
          </PersonalAuthRoute>

          {/*
           Access Tokens can be created with either SSO or Personal
           credentials, so although they are under `/settings/...` we don't
           want to enforce the use of Personal credentials as it could lead to
           incorrect credentials being used when creating tokens.
          */}
          <PrivateRoute
            component={SettingsView}
            exact
            path={[
              routes.PERSONAL_SETTINGS_ACCESS_TOKENS.template(),
              routes.PERSONAL_SETTINGS_ACCESS_TOKENS_CREATE.template(),
              routes.APPEARANCE_SETTINGS.create({}),
            ]}
          />

          {/* Not found default */}
          <Route component={NotFoundView} />
        </Switch>
      </React.Suspense>
    </CoreLayoutWrapper>
  )
}

export default CoreLayout
